import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export const Features = () => {
  const { t } = useTranslation();
  const categories = ['content', 'seo', 'automation', 'premium'];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="features" className="section py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
          {t('features.title')}
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          {t('features.subtitle')}
        </p>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12"
      >
        {categories.map((category) => {
          const categoryData = t(`features.categories.${category}`, { returnObjects: true });
          const items = t(`features.categories.${category}.items`, { returnObjects: true });
          if (!Array.isArray(items)) return null;

          return (
            <motion.div
              key={category}
              variants={itemVariants}
              className="relative"
            >
              <div className="h-full p-8 rounded-2xl bg-white dark:bg-[#131314] border border-gray-100 dark:border-gray-700/50 backdrop-blur-sm shadow-lg">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2 bg-clip-text text-transparent premium-gradient">
                    {categoryData.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {categoryData.description}
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  {items.map((item, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100/40 dark:bg-black flex items-center justify-center text-[var(--gradient-start)] text-sm">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 dark:text-gray-200">
                        {item}
                      </span>
                    </motion.li>
                  ))}
                </ul>

                {category === 'premium' && (
                  <motion.a
                    href="https://calendly.com/alex-craft-with-ai/demo-de-craftseo?month=2025-01"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center justify-center w-full px-6 py-3 rounded-full bg-gradient-to-br from-[var(--gradient-start)] via-[var(--gradient-middle)] to-[var(--gradient-end)] text-white font-medium shadow-lg transition-all duration-300"
                  >
                    {categoryData.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.a>
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}